/**
 * CR-5973: Shared formatting helpers for the Contract Details > History
 * sub-tab. Extracted out of ContractPriceDetailModal.tsx to keep that file
 * under the 500-line cap. The audit-trail layout mirrors the PO line
 * Change Request audit trail
 * (frontend/src/components/purchasing/ChangeRequestsTab.tsx, Change History block).
 */
import {
  FileText, Calendar, DollarSign, User, RefreshCw, AlertTriangle, Clock,
  Bell, Building2, FilePlus2, Edit, Activity, Download,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Humanised labels for raw field names persisted on
 * ContractPriceChangeLog.field_changed. Anything missing falls back to a
 * title-cased version of the snake_case name. The synthetic `created`
 * entry the backend's create() path writes is rendered as "Contract Created".
 */
const HISTORY_FIELD_LABELS: Record<string, string> = {
  created: 'Contract Created',
  notes: 'Notes',
  contract_number: 'Contract Number',
  valid_from: 'Valid From',
  valid_to: 'Valid To',
  currency: 'Currency',
  status: 'Status',
  owner: 'Contract Owner',
  contract_file: 'Contract File',
  auto_renew: 'Auto-Renew',
  renewal_period_years: 'Renewal Period (years)',
  notice_period_months: 'Notice Period (months)',
  reminder_days: 'Reminder (days)',
  risk_category: 'Risk Category',
  br_frequency_months: 'BR Frequency (months)',
  contract_negotiation: 'Contract Negotiation',
  supplier: 'Supplier',
  customer: 'Customer',
  // Customer review 11 Sep (items 21 + 23): type is editable now, and the
  // import / part-band rows had no labels of their own.
  contract: 'Contract Created',
  contract_type: 'Price Book / Contract Type',
  supplier_contract_reference: "Supplier's Contract Reference",
  part: 'Part',
  part_number: 'Part Number',
  description: 'Part Description',
  unit_price: 'Unit Price',
  lot_price: 'Lot Price',
  qty_from: 'Qty From',
  qty_to: 'Qty To',
  unit_of_measure: 'Unit of Measure',
};

export const HISTORY_FIELD_ICONS: Record<string, LucideIcon> = {
  created: FilePlus2,
  notes: FileText,
  contract_number: FileText,
  valid_from: Calendar,
  valid_to: Calendar,
  currency: DollarSign,
  status: Activity,
  owner: User,
  contract_file: Download,
  auto_renew: RefreshCw,
  renewal_period_years: RefreshCw,
  notice_period_months: Clock,
  reminder_days: Bell,
  risk_category: AlertTriangle,
  br_frequency_months: Calendar,
  contract_negotiation: Edit,
  supplier: Building2,
  customer: Building2,
  contract: FilePlus2,
  contract_type: FileText,
  unit_price: DollarSign,
  lot_price: DollarSign,
};

export function getHistoryFieldLabel(field: string): string {
  return (
    HISTORY_FIELD_LABELS[field] ||
    field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

// Boolean fields are persisted to the change log as Python's "True"/"False"
// strings — render them as Yes/No so the audit trail matches the rest of
// the UI vocabulary.
const BOOLEAN_FIELDS = new Set(['auto_renew', 'contract_negotiation']);

// field_changed values that store ISO date strings (e.g. "2027-12-31").
// The PO line audit trail uses localised dates, so we mirror that.
const DATE_FIELDS = new Set(['valid_from', 'valid_to']);

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Convert raw stored ContractPriceChangeLog values into display strings.
 * Booleans become Yes/No; ISO dates pass through the caller's locale-aware
 * formatter; everything else is rendered as-is.
 */
export function humaniseHistoryValue(
  field: string,
  rawValue: string | null | undefined,
  formatDate: (d: string) => string,
): string {
  if (!rawValue) return '';
  if (BOOLEAN_FIELDS.has(field)) {
    if (rawValue === 'True') return 'Yes';
    if (rawValue === 'False') return 'No';
  }
  if (DATE_FIELDS.has(field) && ISO_DATE_RE.test(rawValue)) {
    try { return formatDate(rawValue); } catch { /* fall through */ }
  }
  return rawValue;
}
